import React, { useContext, useMemo } from 'react';
import styled from 'styled-components';
import { useRouter } from 'next/router';
import {useTranslations} from "use-intl";

import { useModal } from '@tager/web-components';
import { Nullable } from '@tager/web-core';

import { colors } from '@/constants/theme';
import DiscountButton from '@/components/Button/DiscountButton';
import OrderForm from '@/components/Forms/OrderForm';
import { getAliasString } from '@/utils/common';
import { useTypedSelector } from '@/store/store';
import {
  selectActiveId,
  selectProductResource,
} from '@/store/reducers/pages/product/product';
import { productContext } from '@/providers/Product';
import { FullProductType } from '@/typings/model';

type Props = {
  product: Nullable<FullProductType>;
};

function Order({ product }: Props) {
  const openModal = useModal();

  const activeIndex = useTypedSelector((state) => selectActiveId(state));
  const { price, discountPrice, optionData } = useContext(productContext);
  const discount = 2;

  const productData = useMemo(() => {
    if (!product) {
      return null;
    }

    const id = product.id;
    const name = product?.name ?? '';
    const image = !!product?.gallery.length
      ? product.gallery[activeIndex]?.image?.preview
      : null;
    const productPrice = price;
    const productDiscountPrice = discountPrice;
    const optionName = product?.multiplePricesEnabled
      ? optionData?.optionName
      : null;
    const optionValue = product?.multiplePricesEnabled
      ? optionData?.optionValue
      : null;
    const hasDiscount = true;

    return {
      id,
      name,
      image,
      price: productPrice,
      discountPrice: productDiscountPrice,
      optionName,
      optionValue,
      hasDiscount,
      notInStock: product.availability === 'OUT_OF_ORDER'
    };
  }, [
    activeIndex,
    product,
    discountPrice,
    optionData?.optionName,
    optionData?.optionValue,
    price,
  ]);


  const handler = () => {
    openModal(
      OrderForm,
      {
        title: t('forms.leave_request'),
        orderData: productData && { ...productData, discount },
      },
      { withAnimation: false, scrollLockDisabled: true }
    );
  };

  const t = useTranslations();

  return (
    <Component>
      <StyledDiscountButton onClick={handler} discount={discount}>
        {t('product.buy_in_click')}
      </StyledDiscountButton>
    </Component>
  );
}

export default Order;

const Component = styled.div`
  margin-top: 20px;
  padding: 6px 15px 15px;
  background: ${colors.grayBackground};
`;

const DiscountText = styled.div`
  text-align: center;
  font-style: normal;
  font-weight: normal;
  font-size: 13px;
  line-height: 24px;
`;

const StyledDiscountButton = styled(DiscountButton)`
  margin: 6px auto 0;
  max-width: 480px;
`;
